<script lang="ts">
  import type { ComponentProps } from "svelte";
  import { dataField } from "../../lib/dataform/dataFormLib.svelte";
  import Fieldset from "./Fieldset.svelte";
  import { partitionNames } from "./util";
  import { deleteBack, inputForward } from "../../lib/input";

  type PropsType = Omit<ComponentProps<typeof Fieldset>, 'children' | 'error'> & {
    name: string;
    required?: boolean;
    min?: string;
    max?: string;
  }

  let {
    legend,
    name,
    required = false,
    min = undefined,
    max = undefined,
    disabled = undefined,
    class : className,
    postfix = null,
    ...rest
  } : PropsType = $props();

  let [inputClass, containerClass] = $derived.by(()=>partitionNames(className, /input-/))
  const field = dataField(name)

  const parts = ((field.value as string) || "--").split("-")
  let day = $state(parts[2]), month = $state(parts[1]), year = $state(parts[0]);
  let input = $state({
    day: null,
    month: null,
    year: null
  })

  const nativeDate = (()=>{
    const el = document.createElement("input")
    el.type = "date"
    return el
  })();
  $effect(()=>{
    nativeDate.min = min
    nativeDate.max = max
    let value = day && month && year && 
      `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    if(!value) return void(field.value = "");

    let eachInputValid = true
    for(const field of [input.year, input.month, input.day]){
      field.setCustomValidity("");
      if(!field.checkValidity()){
        eachInputValid = false;
        field.setCustomValidity("Must be a valid date");
      }
    }
    if(!eachInputValid) return void(field.value = "");

    nativeDate.value = value
    if(nativeDate.value == value) {
      // Combined value is a well-formed date
      field.value = value
      nativeDate.checkValidity()
      let cv = ""
      if(nativeDate.validity.rangeUnderflow) cv = "Date is too early";
      if(nativeDate.validity.rangeOverflow) cv = "Date is too recent";
      input.year.setCustomValidity(cv);
      if(!cv && !nativeDate.validity.valid) input.day.setCustomValidity("Must be a valid date");
    } else {
      // Not a well-formed date, means the day though plausible isn't valid for this month
      input.day.setCustomValidity("Invalid date month mismatch");
      field.value = ""
    }
  })
</script>
<Fieldset error={field.error as string} {legend} {disabled} class={containerClass} {postfix} {...rest}>
  <label class="ui-input focus-within:outline-none focus-within:border-blue-600 {inputClass} flex items-center gap-1 relative">
    <input type="text" bind:this={input.day} bind:value={day} {@attach inputForward(input.month, (v)=>parseInt(v) > 3)} inputmode="numeric" pattern={"0?[1-9]|1\\d|2\\d|30|31"} maxlength="2" class="w-[2ch] grow text-center p-0 focus:outline-none" placeholder="DD" required={!!(required || month || year)} />
    /
    <input type="text" bind:this={input.month} bind:value={month} {@attach inputForward(input.year, (v)=>parseInt(v) > 1)} {@attach deleteBack(input.day)} inputmode="numeric" pattern={"0?[1-9]|10|11|12"} maxlength="2" class="w-[2ch] grow text-center p-0 focus:outline-none" placeholder="MM" required={!!(required || day || year)} />
    /
    <input type="text" bind:this={input.year} bind:value={year} {@attach deleteBack(input.month)} inputmode="numeric" pattern={"\\d{1,4}"} maxlength="4" class="w-[4ch] grow text-center p-0 focus:outline-none" placeholder="YYYY" required={!!(required || day || month)} />
  </label>
  <input type="hidden" name={field.name} value={field.value} />
</Fieldset>